import { Button, InlineLoading } from '@carbon/react';
import { OpenPanelFilledLeft, OpenPanelLeft } from '@carbon/react/icons';
import { FloatingTooltip } from 'components/floating-tooltip';
import style from './action-bar.module.scss';
import { CreateDcaWorkspace } from 'components/dca-workspace/create-dca-workspace';
import { AgentTriggerButton } from 'components/agent-chat';
import { EVAL_AGENT } from 'components/agent-chat/agents/eval-agent';

interface ActionBarProps {
  isLeftPanelCollapsed: boolean;
  onToggleLeftPanel: () => void;
  onRename: () => void;
  isRenameVisible: boolean;
  onSave: () => void;
  onCalculate: () => void;
  isSaveVisible: boolean;
  isSaveDisabled: boolean;
  isSaveLoading: boolean;
  isCalculateDisabled: boolean;
  calculateDisabledReason?: string;
  isCalculateLoading: boolean;
  packageId: number | null;
  /** False when the Eval 3 Agent is not seeded in this environment. */
  isAgentAvailable?: boolean;
  /** The DCA scenario the eval shows (the selected case's); a new DCA workspace opens on it. */
  dcaScenarioId?: number | null;
}

/**
 * Horizontal action bar at the top of the Eval 3.0 main content area.
 * Contains a left-panel toggle and Save/Calculate buttons.
 *
 * @param {ActionBarProps} props - Component props.
 * @return {JSX.Element} The action bar.
 */
export const ActionBar = ({
  isLeftPanelCollapsed,
  onToggleLeftPanel,
  onRename,
  isRenameVisible,
  onSave,
  onCalculate,
  isSaveVisible,
  isSaveDisabled,
  isSaveLoading,
  isCalculateDisabled,
  calculateDisabledReason,
  isCalculateLoading,
  packageId,
  isAgentAvailable = false,
  dcaScenarioId = null,
}: ActionBarProps): JSX.Element => {
  const calculateButton = isCalculateLoading ? (
    <Button kind="primary" size="sm" disabled>
      <InlineLoading description="Calculating..." />
    </Button>
  ) : (
    <Button
      kind="primary"
      size="sm"
      onClick={onCalculate}
      disabled={isCalculateDisabled}
    >
      Calculate
    </Button>
  );

  return (
    <div className={style.actionBar}>
      <Button
        kind="ghost"
        size="sm"
        renderIcon={isLeftPanelCollapsed ? OpenPanelLeft : OpenPanelFilledLeft}
        onClick={onToggleLeftPanel}
        className={style.panelToggle}
      >
        {isLeftPanelCollapsed ? 'Show Left Panel' : 'Hide Left Panel'}
      </Button>
      <div className={style.buttons}>
        {isAgentAvailable && <AgentTriggerButton agent={EVAL_AGENT} />}
        {isRenameVisible && (
          <Button kind="primary" size="sm" onClick={onRename}>
            Rename Workspace
          </Button>
        )}

        {packageId && (
          <CreateDcaWorkspace
            size="sm"
            parentType="Package"
            parentId={packageId}
            additionalData={{ dcaScenarioId }}
          />
        )}

        {isSaveVisible &&
          (isSaveLoading ? (
            <Button kind="tertiary" size="sm" disabled>
              <InlineLoading description="Saving..." />
            </Button>
          ) : (
            <Button
              kind="tertiary"
              size="sm"
              onClick={onSave}
              disabled={isSaveDisabled}
            >
              Save
            </Button>
          ))}

        {isCalculateDisabled && calculateDisabledReason ? (
          <FloatingTooltip
            placement="bottom"
            content={<p>{calculateDisabledReason}</p>}
          >
            {calculateButton}
          </FloatingTooltip>
        ) : (
          calculateButton
        )}
      </div>
    </div>
  );
};
